import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './TestimonialSection.css';

// Image protection handlers
const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
};

const handleDragStart = (e: any) => {
    e.preventDefault();
    return false;
};

const testimonials = [
    {
        id: 1,
        text: "Studio Z26 captures weddings with clarity, creative pre-wedding shoots, perfect candid moments, and cinematic videos, delivered on time, within budget.",
        author: "Dib",
        location: "",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785657/Reviewer1_ajabtg.jpg",
        rating: 5
    },
    {
        id: 2,
        text: "Best in the market, I highly recommend Studio Z26. Owner Deba da and the team are professional and welcoming, making you feel like family.",
        author: "Sroboni Nayek Das",
        location: "",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785696/Sraboni_fb8qsm.jpg",
        rating: 5
    },
    {
        id: 3,
        text: "This team is really good. Covered all the events of my sister's wedding very well. And the behavior is also very good. Thanks Studio Z26",
        author: "Koushik",
        location: "",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785706/Kaoushik_nqvtkp.jpg",
        rating: 5
    }
];

const TestimonialSection: React.FC = () => {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    const scrollToVideoReviews = () => {
        // Navigate to the VideoReviewsPage
        navigate('/video-reviews');
    };

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    const headerY = useTransform(scrollYProgress, [0.05, 0.2], [80, 0]);
    const headerOpacity = useTransform(scrollYProgress, [0.05, 0.2], [0, 1]);

    const nextCard = () => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    };

    const prevCard = () => {
        setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    const minSwipeDistance = 30;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            nextCard();
        } else if (isRightSwipe) {
            prevCard();
        }
    };

    return (
        <div className="testimonial-section" id="testimonials" ref={containerRef}>
            <div className="testimonial-container">
                <motion.div
                    className="testimonial-header"
                    style={{ y: headerY, opacity: headerOpacity }}
                >
                    <h2 className="section-title dark">
                        LOVE NOTES <span className="title-accent">FROM</span>
                    </h2>
                    <h2 className="section-title dark indent">
                        AROUND THE <span className="title-accent">WORLD</span>
                    </h2>
                </motion.div>

                {/* Desktop Grid View */}
                <div className="testimonial-grid desktop-view">
                    {testimonials.map((item, index) => (
                        <TestimonialCard
                            key={item.id}
                            item={item}
                            index={index}
                            progress={scrollYProgress}
                        />
                    ))}
                </div>

                {/* Mobile Carousel View */}
                <div className="mobile-view">
                    <div className="carousel-container">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentIndex}
                                initial={{ opacity: 0, x: 100 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -100 }}
                                transition={{ duration: 0.3 }}
                                className="carousel-card"
                                onTouchStart={onTouchStart}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                            >
                                <TestimonialCard
                                    item={testimonials[currentIndex]}
                                    index={0}
                                    progress={scrollYProgress}
                                />
                            </motion.div>
                        </AnimatePresence>
                        
                        <div className="carousel-controls">
                            <button className="carousel-btn prev" onClick={prevCard}>
                                ←
                            </button>
                            <div className="carousel-dots">
                                {testimonials.map((_, index) => (
                                    <button
                                        key={index}
                                        className={`dot ${index === currentIndex ? 'active' : ''}`}
                                        onClick={() => setCurrentIndex(index)}
                                    />
                                ))}
                            </div>
                            <button className="carousel-btn next" onClick={nextCard}>
                                →
                            </button>
                        </div>
                    </div>
                </div>

                <motion.div 
                    className="cta-section"
                    style={{ 
                        opacity: useTransform(scrollYProgress, [0.4, 0.6], [0, 1]),
                        y: useTransform(scrollYProgress, [0.4, 0.6], [50, 0])
                    }}
                >
                    <button className="cta-button" onClick={scrollToVideoReviews}>Watch Video Reviews</button>
                </motion.div>
            </div>
        </div>
    );
};

const TestimonialCard: React.FC<{
    item: typeof testimonials[0];
    index: number;
    progress: any;
}> = ({ item, index, progress }) => {
    // Helper function to wrap punctuation and numbers with default font
    const wrapWithDefaultFont = (text: string) => {
        return text.split(/([.,;:!?'"()[\]{}0-9]+)/).map((part, index) => {
            if (/[.,;:!?'"()[\]{}]/.test(part) || /\d/.test(part)) {
                return <span key={index} className="default-font">{part}</span>;
            }
            return part;
        });
    };

    // More sensitive scroll ranges with increased movement
    const yRange = [150 + (index * 80), -30];
    const opacityRange = [0, 1];
    const scaleRange = [0.8, 1];
    const start = 0.05 + (index * 0.05);
    const end = 0.3 + (index * 0.08);

    const y = useTransform(progress, [start, end], yRange);
    const opacity = useTransform(progress, [start, end], opacityRange);
    const scale = useTransform(progress, [start, end], scaleRange);

    return (
        <motion.div
            className={`testimonial-card card-${index + 1}`}
            style={{ y, opacity, scale }}
        >
            <div className="testimonial-number">{String(item.id).padStart(2, '0')}</div>
            <div className="testimonial-content">
                <div className="location-badge">{wrapWithDefaultFont(item.location)}</div>
                <p className="testimonial-text">{wrapWithDefaultFont(item.text)}</p>
                <div className="author-section">
                    <div className="author-details">
                        <h4 className="author">{wrapWithDefaultFont(item.author)}</h4>
                        <div className="rating">
                            {[...Array(item.rating)].map((_, i) => (
                                <span key={i} className="star">★</span>
                            ))}
                        </div>
                    </div>
                    {item.image && (
                        <div className="author-image">
                            <img 
                                src={item.image} 
                                alt={item.author} 
                                className="avatar"
                                onContextMenu={handleContextMenu}
                                onDragStart={handleDragStart}
                                style={{ pointerEvents: 'none' }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default TestimonialSection;
